import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, ShieldAlert, Plus, FileText, FileType2, Loader2, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { downloadBlob, isIOS } from '../../utils/downloadBlob';

const API = process.env.REACT_APP_BACKEND_URL;

const WARNING_TYPES = [
  { value: 'verbal', label: 'Verbal Warning' },
  { value: 'written', label: 'Written Warning' },
  { value: 'written2', label: 'Written Warning #2' },
  { value: 'final', label: 'Final Warning' },
];
const WT_LABEL = Object.fromEntries(WARNING_TYPES.map((w) => [w.value, w.label]));

const fmt = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };

const STATUS = {
  accepted: { label: 'Accepted', cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
  objected: { label: 'Objected', cls: 'bg-red-100 text-red-700', Icon: XCircle },
  pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-700', Icon: Clock },
};

const PortalManagerWarnings = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [staff, setStaff] = useState([]);
  const [department, setDepartment] = useState('');
  const [rows, setRows] = useState([]);
  const [offenseTypes, setOffenseTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [saving, setSaving] = useState(false);

  // form
  const [form, setForm] = useState({
    employee_id: '', warning_type: 'written', subject: '',
    offense_types: [], description: '', plan_for_improvement: '',
    request_suspension: false, suspension_request_reason: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, w, m] = await Promise.all([
        axios.get(`${API}/api/employee-portal/manager/warning-staff`, { headers }),
        axios.get(`${API}/api/employee-portal/manager/warnings-issued`, { headers }),
        axios.get(`${API}/api/warning-letters/offense-types`),
      ]);
      setStaff(s.data?.staff || []);
      setDepartment(s.data?.department || '');
      setRows(w.data?.items || []);
      setOffenseTypes(m.data?.offense_types || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/employee-portal/login');
      else if (e?.response?.status === 403) toast({ title: 'Managers only', description: 'This area is for department managers.', variant: 'destructive' });
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({
    employee_id: '', warning_type: 'written', subject: '',
    offense_types: [], description: '', plan_for_improvement: '',
    request_suspension: false, suspension_request_reason: '',
  });

  const toggleOffense = (val) => setForm((f) => ({
    ...f, offense_types: f.offense_types.includes(val)
      ? f.offense_types.filter((o) => o !== val)
      : [...f.offense_types, val],
  }));

  const submit = async () => {
    if (!form.employee_id) { toast({ title: 'Select a staff member', variant: 'destructive' }); return; }
    if (!form.description.trim()) { toast({ title: 'Description is required', variant: 'destructive' }); return; }
    if (form.request_suspension && !form.suspension_request_reason.trim()) {
      toast({ title: 'Suspension reason required', description: 'Add a reason for the HR suspension request.', variant: 'destructive' }); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/employee-portal/manager/warnings`, form, { headers });
      toast({ title: 'Warning issued', description: 'The staff member has been notified to Accept or Object.' });
      setDlg(false); resetForm(); load();
    } catch (e) {
      toast({ title: 'Could not issue', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const download = async (r, kind) => {
    try {
      const ext = kind === 'docx' ? 'docx' : 'pdf';
      const mime = kind === 'docx'
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : 'application/pdf';
      const res = await axios.get(
        `${API}/api/employee-portal/manager/warnings/${r.employee_id}/${r.warning_id}/${ext}`,
        { headers, responseType: 'blob' });
      downloadBlob(res.data, `Warning_${(r.employee_name || '').replace(/\s+/g, '_')}.${ext}`, mime);
      if (kind === 'pdf' && isIOS()) toast({ title: 'PDF opened', description: 'Tap Share to save or print.' });
    } catch { toast({ title: 'Download failed', variant: 'destructive' }); }
  };

  return (
    <div className="p-4 max-w-3xl mx-auto" data-testid="portal-manager-warnings">
      <button onClick={() => navigate('/employee-portal')} className="flex items-center gap-1 text-sm text-violet-600 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </button>

      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-600" /> Issue Warnings
          </h1>
          <p className="text-sm text-gray-500">Issue disciplinary letters to your <b>{department || 'department'}</b> staff. Ignored warnings auto-accept after 5 days.</p>
        </div>
        <Button onClick={() => { resetForm(); setDlg(true); }} className="bg-amber-600 hover:bg-amber-700 shrink-0" data-testid="open-issue-warning-btn">
          <Plus className="h-4 w-4 mr-1" /> Issue
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : rows.length === 0 ? (
        <Card className="mt-4"><CardContent className="py-12 text-center text-gray-500">You haven't issued any warning letters yet.</CardContent></Card>
      ) : (
        <div className="mt-4 overflow-x-auto border rounded-lg bg-white">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2 w-10">S. No</th>
                <th className="px-3 py-2">Employee</th>
                <th className="px-3 py-2">Issue Date</th>
                <th className="px-3 py-2">Subject</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Accepted</th>
                <th className="px-3 py-2 text-right">Letter</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const st = STATUS[r.response_status] || STATUS.pending;
                return (
                  <tr key={r.warning_id} className="border-t align-top" data-testid={`issued-warning-${r.warning_id}`}>
                    <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-800">{r.employee_name}</div>
                      <div className="text-[11px] text-gray-400">{r.employee_code} · {WT_LABEL[r.warning_type] || r.warning_type}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmt(r.issue_date)}</td>
                    <td className="px-3 py-2 text-gray-700">{r.subject || <span className="text-gray-300">—</span>}</td>
                    <td className="px-3 py-2">
                      <Badge className={`${st.cls} text-xs`}><st.Icon className="h-3 w-3 mr-1" />{st.label}</Badge>
                      {r.response_status === 'objected' && r.objection_reason && (
                        <div className="mt-1 text-[11px] text-red-600 max-w-[200px]">
                          <b>Objected:</b> “{r.objection_reason}”
                        </div>
                      )}
                      {r.suspension_requested && (
                        <div className="mt-1 text-[10px] text-purple-600">Suspension requested to HR</div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                      {r.response_status === 'accepted'
                        ? <>{fmt(r.accepted_at)}{r.auto_accepted ? <span className="block text-[10px] text-gray-400">(auto, 5-day)</span> : null}</>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1 justify-end">
                        <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => download(r, 'pdf')} data-testid={`dl-pdf-${r.warning_id}`}>
                          <FileText className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => download(r, 'docx')} data-testid={`dl-docx-${r.warning_id}`}>
                          <FileType2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Issue dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="issue-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> Issue Warning Letter</DialogTitle>
            <DialogDescription>Issue a disciplinary letter to a staff member in your department. The employee is notified to Accept or Object.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Staff member *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="warn-staff-select"><SelectValue placeholder="Select staff…" /></SelectTrigger>
                <SelectContent>
                  {staff.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} · {s.employee_code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type of warning *</Label>
              <Select value={form.warning_type} onValueChange={(v) => setForm((f) => ({ ...f, warning_type: v }))}>
                <SelectTrigger className="mt-1" data-testid="warn-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WARNING_TYPES.map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject *</Label>
              <Input className="mt-1" placeholder="e.g. Repeated lateness" value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} data-testid="warn-subject-input" />
            </div>
            <div>
              <Label>Type of offense</Label>
              <div className="mt-1 grid grid-cols-1 gap-1.5 bg-gray-50 border rounded-lg p-2.5">
                {offenseTypes.map((o) => (
                  <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.offense_types.includes(o.value)} onCheckedChange={() => toggleOffense(o.value)} data-testid={`offense-${o.value}`} />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Description of violation *</Label>
              <Textarea className="mt-1" rows={3} placeholder="Describe what happened…" value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="warn-description-input" />
            </div>
            <div>
              <Label>Plan for improvement (optional)</Label>
              <Textarea className="mt-1" rows={2} value={form.plan_for_improvement}
                onChange={(e) => setForm((f) => ({ ...f, plan_for_improvement: e.target.value }))} data-testid="warn-plan-input" />
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <label className="flex items-center gap-2 text-sm font-medium text-purple-800 cursor-pointer">
                <Checkbox checked={form.request_suspension}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, request_suspension: !!v }))} data-testid="warn-request-suspension" />
                Request a suspension from HR
              </label>
              {form.request_suspension && (
                <Textarea className="mt-2 bg-white" rows={2} placeholder="Reason for the suspension request (sent to HR)…"
                  value={form.suspension_request_reason}
                  onChange={(e) => setForm((f) => ({ ...f, suspension_request_reason: e.target.value }))} data-testid="warn-suspension-reason" />
              )}
              <p className="text-[11px] text-purple-500 mt-1">Managers cannot apply a suspension directly — HR reviews the request.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" disabled={saving} onClick={submit} data-testid="submit-warning-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldAlert className="h-4 w-4 mr-1" />} Issue Warning
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalManagerWarnings;
